import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTabURLState } from './useTabURLState';

interface UseTabURLSyncOptions {
  tabParamName?: string;
  defaultTab?: string;
}

export const useTabURLSync = (options: UseTabURLSyncOptions = {}) => {
  const { tabParamName = 'tab', defaultTab = '' } = options;
  const location = useLocation();
  const { currentTab, updateTabParams } = useTabURLState({ tabParamName, defaultTab });

  // Function to sync URL parameters with the current tab
  const syncURLParams = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const currentTabFromURL = params.get(tabParamName);
    
    // If we're on the current tab, ensure URL reflects the current state
    if (currentTabFromURL === currentTab) {
      // This will trigger a re-render with the current URL parameters
      return;
    }
  }, [location.search, currentTab, tabParamName]);

  // Sync URL parameters when tab changes
  useEffect(() => {
    syncURLParams();
  }, [currentTab, syncURLParams]);

  // Function to update parameters for the current tab
  const updateCurrentTabParams = useCallback((newParams: Record<string, string>) => {
    updateTabParams(newParams);
  }, [updateTabParams]);

  // Function to get current tab's stored parameters
  const getCurrentTabParams = useCallback(() => {
    try {
      const stored = sessionStorage.getItem(`tab_params_${currentTab}`);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  }, [currentTab]);

  return {
    currentTab,
    updateCurrentTabParams,
    syncURLParams,
    getCurrentTabParams,
  };
}; 